import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react';
import isMobileDevice from 'ismobilejs';
import type { GetServerSideProps, NextPage } from 'next';
import NextHead from 'next/head';
import { useRouter } from 'next/router';
import { ChangeEvent, useCallback } from 'react';
import * as yup from 'yup';
import { getPublicRuntimeConfig } from '../../common/config';
import {
  ArticleStatus,
  ArticleType,
  shouldNotShow,
} from '../../common/libs/article';
import { DiscloseScope } from '../../common/libs/discloseScope';
import { sendAnalyticsEvent } from '../../common/libs/googleAnalytics';
import { title } from '../../common/libs/metadata';
import {
  SORT_ORDER_LIKE_COUNT_DESC,
  SORT_ORDER_SESSION_COUNT_DESC,
  SORT_ORDER_UPDATE_DATE_DESC,
  SortOrder,
} from '../../common/libs/sortOrder';
import ArticlesGrid, { ArticleHeader } from '../../components/ArticlesGrid';
import TagButtonLink from '../../components/TagButtonLink';
import TargetReaderSelect, {
  TargetReaderType,
} from '../../components/TargetReaderSelect';
import * as date from '../../server/libs/date';
import { getGaCid } from '../../server/libs/googleAnalytics';
import { fetchAccountIntegrationProfiles } from '../../server/libs/id';
import prisma from '../../server/libs/prisma';
import withAuth from '../../server/libs/withAuth';
import { findArticlesOrderByUpdateDate } from '../../server/libs/articles';

type SortTargetColumn = 'update_date' | 'session_count' | 'like_count';

type PageContext = {
  tagSearchedArticles: ArticleHeader[];
  sortOrder: SortOrder;
  tags: Tag[];
  readerTypeIds: number[];
  targetReaderTypes: TargetReaderType[];
  isMobile: boolean;
};

export type Tag = {
  id: number;
  name: string | null;
};

export function tagIds(tags: Tag[]): number[] {
  return tags.map(({ id }) => id);
}

export function tagNames(tags: Tag[]): string[] {
  return tags.filter(({ name }) => !!name).map(({ name }) => name ?? '');
}

export function tagsSearchPath(tags: Tag[]): string {
  return `/tagsearch/${encodeURIComponent(tagIds(tags).join(','))}`;
}

function tagsBracketsString(tags: Tag[]): string {
  return tagNames(tags)
    .map((name) => `「#${name}」`)
    .join('');
}

function createTagsSearchUrl(tags: Tag[]) {
  const { slideWebBaseUrl } = getPublicRuntimeConfig();
  return `${slideWebBaseUrl}${tagsSearchPath(tags)}`;
}

function PageHead({ tags, noindex }: { tags: Tag[]; noindex: boolean }) {
  const titleName = !!tags.length
    ? `${tagsBracketsString(tags)}タグがつけられたスライド一覧`
    : '検索したタグのスライド一覧';
  const tagsSearchUrl = createTagsSearchUrl(tags);
  return (
    <NextHead>
      <title>{title(titleName)}</title>
      <link rel="canonical" href={tagsSearchUrl} />
      {noindex && <meta name="robots" content="noindex" />}
    </NextHead>
  );
}

function TagSearchedArticles({
  articles,
  sortOrder,
  tags,
  readerTypeIds,
  targetReaderTypes,
  isMobile,
}: {
  articles: ArticleHeader[];
  sortOrder: SortOrder;
  tags: Tag[];
  readerTypeIds: number[];
  targetReaderTypes: TargetReaderType[];
  isMobile: boolean;
}) {
  const router = useRouter();
  const onSelectChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const additionalQueryParam =
        readerTypeIds && readerTypeIds.length > 0
          ? `&readerTypeIds=${JSON.stringify(readerTypeIds)}`
          : '';
      router.push(
        `${tagsSearchPath(tags)}?sortOrder=${
          e.target.value
        }${additionalQueryParam}`,
      );
    },
    [readerTypeIds, router, tags],
  );

  const targetReaderTypesForView = targetReaderTypes.map(
    (targetReaderType) => ({
      ...targetReaderType,
      selected: readerTypeIds.includes(targetReaderType.id),
    }),
  );

  const onTargetReadersChange = useCallback(
    (readerTypeIds: number[]) => {
      const additionalQueryParam =
        readerTypeIds.length > 0
          ? `&readerTypeIds=${JSON.stringify(readerTypeIds)}`
          : '';
      router.push(
        `${tagsSearchPath(tags)}?sortOrder=${sortOrder}${additionalQueryParam}`,
      );
    },
    [router, sortOrder, tags],
  );

  const onClickTargetReaders = (event: ChangeEvent<HTMLInputElement>) => {
    const targetReaderId = Number.parseInt(event.target.id);
    let modifiedReaderTypeIds;
    if (readerTypeIds.includes(targetReaderId)) {
      modifiedReaderTypeIds = readerTypeIds.filter((s) => s !== targetReaderId);
    } else {
      modifiedReaderTypeIds = [...readerTypeIds, targetReaderId];
    }
    onTargetReadersChange(modifiedReaderTypeIds);
  };

  return (
    <>
      <Heading as="h1" fontSize="2xl">
        {tagsBracketsString(tags)}タグがつけられたスライド一覧
      </Heading>
      <Flex
        justify="space-between"
        w="full"
        wrap="wrap"
        alignItems="center"
        gap={2}
      >
        <TargetReaderSelect
          targetReaderTypes={targetReaderTypesForView}
          onChange={onClickTargetReaders}
        />
        <FormControl as={Flex} w="auto" align="center">
          <FormLabel m={0}>表示順：</FormLabel>
          <Select
            w="200px"
            h="3em"
            fontSize="sm"
            value={sortOrder}
            onChange={onSelectChange}
            onFocus={() => sendAnalyticsEvent('slide', 'sort', null)}
          >
            <option value={SORT_ORDER_UPDATE_DATE_DESC}>
              公開日／更新日（新しい順）
            </option>
            <option value={SORT_ORDER_SESSION_COUNT_DESC}>
              ページ閲覧数（多い順）
            </option>
            <option value={SORT_ORDER_LIKE_COUNT_DESC}>
              保存済み数（多い順）
            </option>
          </Select>
        </FormControl>
      </Flex>
      <Flex w="100%" justify="space-between" wrap="wrap">
        <Text flexShrink={0} flexGrow={0} flexBasis="auto">
          検索したタグ：
        </Text>
        <Flex flex="1" wrap="wrap" ml={2}>
          {tags.map((tag) => (
            <Box key={tag.id} mb={1} mr={1}>
              <TagButtonLink tag={tag} />
            </Box>
          ))}
        </Flex>
        <Text flexShrink={0} flexGrow={0} flexBasis="auto">
          検索結果：{articles.length}件
        </Text>
      </Flex>
      {articles.length > 0 ? (
        <ArticlesGrid
          articles={articles}
          isMobile={isMobile}
          onShowThumbnail={(articleId) => {
            sendAnalyticsEvent(
              'slide_thumbnail',
              'show',
              `tagsearch#${articleId}`,
            );
          }}
          onClick={(articleId) => {
            sendAnalyticsEvent(
              'slide_thumbnail',
              'click',
              `tagsearch#${articleId}`,
            );
          }}
        />
      ) : (
        <Text>
          {tagsBracketsString(tags)}に該当するスライドが見つかりませんでした。
        </Text>
      )}
    </>
  );
}

const SearchArticlesByTagPage: NextPage<PageContext> = ({
  tagSearchedArticles,
  sortOrder,
  tags,
  readerTypeIds,
  targetReaderTypes,
  isMobile,
}) => {
  const noindex = tagSearchedArticles.length < 5;

  return (
    <>
      <PageHead tags={tags} noindex={noindex} />
      <VStack spacing={4} align="flex-start">
        <TagSearchedArticles
          articles={tagSearchedArticles}
          sortOrder={sortOrder}
          tags={tags}
          readerTypeIds={readerTypeIds}
          targetReaderTypes={targetReaderTypes}
          isMobile={isMobile}
        />
      </VStack>
    </>
  );
};

export default SearchArticlesByTagPage;

async function findTagsByIds(tagIds: number[]) {
  return prisma.tag.findMany({
    where: {
      tag_id: {
        in: tagIds,
      },
      tag_name: {
        not: null,
      },
    },
    select: {
      tag_id: true,
      tag_name: true,
    },
  });
}

async function createUserAccessHistory(
  userId?: number,
  userAgent?: string,
  gaCid?: string,
) {
  return await prisma.user_access_histories.create({
    data: {
      user_id: userId,
      ga_cid: gaCid,
      user_agent: userAgent,
      operation: 'tagsearch',
    },
  });
}

async function createTagSearchHistory(
  userAccessHistoriesId: number,
  tagId: number,
  tagName: string,
  hitCount: number,
) {
  await prisma.tag_search_histories.create({
    data: {
      user_access_histories_id: userAccessHistoriesId,
      tag_id: tagId,
      tag_name: tagName,
      hit_count: hitCount,
    },
  });
}

export const getServerSideProps: GetServerSideProps<PageContext> = withAuth(
  async (context) => {
    let tagIds: number[];
    try {
      tagIds =
        yup
          .array()
          .of(yup.number().required())
          .validateSync(
            decodeURIComponent(context.params?.tagIds as string).split(','),
          ) ?? [];
    } catch (e) {
      return {
        notFound: true,
      };
    }

    if (!tagIds.length) {
      return {
        notFound: true,
      };
    }

    const readerTypeIds =
      yup
        .array()
        .of(yup.number().required())
        .nullable()
        .validateSync(context.query.readerTypeIds) || null;

    let tags = await findTagsByIds(tagIds);
    if (!tags.length) {
      return {
        notFound: true,
      };
    }

    let sortOrder: SortOrder;
    switch (context.query.sortOrder) {
      case SORT_ORDER_UPDATE_DATE_DESC:
        sortOrder = SORT_ORDER_UPDATE_DATE_DESC;
        break;
      case SORT_ORDER_SESSION_COUNT_DESC:
        sortOrder = SORT_ORDER_SESSION_COUNT_DESC;
        break;
      default:
        sortOrder = SORT_ORDER_LIKE_COUNT_DESC;
        break;
    }

    let sortTargetColumn: SortTargetColumn;
    switch (sortOrder) {
      case SORT_ORDER_UPDATE_DATE_DESC:
        sortTargetColumn = 'update_date';
        break;
      case SORT_ORDER_SESSION_COUNT_DESC:
        sortTargetColumn = 'session_count';
        break;
      case SORT_ORDER_LIKE_COUNT_DESC:
        sortTargetColumn = 'like_count';
        break;
    }

    const tagConnections = await prisma.article_tag_connection.findMany({
      select: {
        article_id: true,
      },
      where: {
        tag_id: {
          in: tagIds,
        },
      },
    });

    const articleIdList = tagConnections.map((elm) => elm.article_id);

    const targetReaderTypes = await prisma.target_reader_types
      .findMany({
        orderBy: {
          display_order: 'asc',
        },
      })
      .then((targetReaderTypes) =>
        targetReaderTypes.map((readerType) => ({
          id: readerType.id,
          name: readerType.name,
        })),
      );

    let tagSearchedArticles: ArticleHeader[] = [];
    if (sortOrder === SORT_ORDER_UPDATE_DATE_DESC) {
      const res = await findArticlesOrderByUpdateDate({
        articleIds: articleIdList,
        readerTypeIds,
        limit: 500,
        req: context.req,
      });
      tagSearchedArticles = res[0];
    } else {
      tagSearchedArticles = await findArticles(
        articleIdList,
        sortTargetColumn,
        readerTypeIds,
        context.req,
      );
    }

    const userId = context.req.user?.id;
    const userAgent = context.req?.headers['user-agent'] ?? '';
    const uah = await createUserAccessHistory(
      userId,
      userAgent,
      getGaCid(context.req),
    );
    tags.forEach((tag) => {
      createTagSearchHistory(
        uah.id,
        tag.tag_id,
        tag.tag_name ?? '',
        tagSearchedArticles.length,
      );
    });

    const isMobile = isMobileDevice(context.req.headers['user-agent']).any;

    return {
      props: {
        tagSearchedArticles,
        sortOrder,
        tags: tags.map((tag) => ({
          id: tag.tag_id,
          name: tag.tag_name,
        })),
        readerTypeIds: readerTypeIds || [],
        targetReaderTypes,
        isMobile,
      },
    };
  },
);

async function findArticles(
  articleIds: string[],
  sortTargetColumn: SortTargetColumn,
  readerTypeIds: number[] | null,
  req: any,
): Promise<ArticleHeader[]> {
  const articleHeaders = await prisma.article_header.findMany({
      take: 500,
      select: {
        article_id: true,
        name: true,
        article_type: true,
        status: true,
        top_img_m: true,
        session_count: true,
        like_count: true,
        disclose_scope: true,
        create_date: true,
        update_date: true,
        user_article_header_author_idTouser: true,
        article_tag_connection: {
          select: {
            tag: true,
          },
        },
        article_page: {
          select: {
            like_count: true,
          },
        },
        article_comments: {
          select: {
            id: true,
          },
        },
      },
      where: {
        article_id: {
          in: articleIds,
        },
        status: 'DONE',
        hidden: false,
        is_deleted: false,
        article_has_target_readers:
          readerTypeIds && readerTypeIds.length > 0
            ? {
                some: {
                  target_reader_type_id: {
                    in: readerTypeIds,
                  },
                },
              }
            : undefined,
      },
      orderBy: {
        [sortTargetColumn]: 'desc',
      },
    });
    // 投稿者がアカウント統合済みであればID基盤側のプロフィール情報を表示させる
    const discloseIds = articleHeaders
      .map((v) => v.user_article_header_author_idTouser.disclose_id ?? '')
      .filter((v) => !!v);
    // アカウント統合済みのユーザのプロフィールのみ返却される
    const idBaseProfileMap = await fetchAccountIntegrationProfiles(discloseIds);

    let tagSearchedArticles = articleHeaders.reduce(
      (articles: any, article: any) => {
        const author = article.user_article_header_author_idTouser;
        const idBaseProfile = idBaseProfileMap[author.disclose_id ?? ''];

        if (
          shouldNotShow(
            idBaseProfile,
            req.user,
            req.isAuthenticated(),
            article,
          )
        ) {
          return articles;
        }

        articles.push({
          articleId: article.article_id,
          name: article.name,
          articleType: article.article_type as ArticleType,
          status: article.status as ArticleStatus | null,
          topImageM: article.top_img_m,
          sessionCount: article.session_count || 0,
          likeCount:
            article.like_count +
            article.article_page.reduce(
              (prev: any, curr: any) => prev + curr.like_count,
              0,
            ),
          commentCount: article.article_comments.length,
          discloseScope: article.disclose_scope as DiscloseScope | null,
          createDate: date.toIso8601Jst(article.create_date),
          updateDate: date.toIso8601Jst(article.update_date),
          author: {
            userId: article.user_article_header_author_idTouser.user_id,
            userName: idBaseProfile?.userName ?? null,
            // userNameが設定されている場合はfirstName, lastNameはフロントに返さないようにする
            lastName: idBaseProfile?.userName
              ? null
              : idBaseProfile?.lastName ?? author.last_name,
            firstName: idBaseProfile?.userName
              ? null
              : idBaseProfile?.firstName ?? author.first_name,
            updateDateEpochMilliseconds:
              article.user_article_header_author_idTouser.update_date.getTime(),
            profilePhotoS3Key:
              article.user_article_header_author_idTouser.profile_photo_s3_key,
            idBaseProfileUrl: idBaseProfile?.profilePhotoUrl ?? null,
            antaaAccountType: idBaseProfile?.accountType ?? null,
            antaaAccountSubType: idBaseProfile?.accountSubType ?? null,
            isOfficialAccount: idBaseProfile?.isOfficialAccount ?? null,
          },
          tags: article.article_tag_connection.map((conn: any) => ({
            id: conn.tag.tag_id,
            name: conn.tag.tag_name,
          })),
        });
        return articles;
      },
      [],
    );
    if (sortTargetColumn === 'like_count') {
      // HACK: 保存総数でソートし直し
      // ソート順に保存数が指定された場合「スライド保存数」でソートされた状態になっている。
      // 「保存総数＝スライド保存数＋ページ保存数」でソートされた状態になるように再ソートする。
      // 本来はSQLでやるべきだが、rawQueryを書く必要があり、実装工数が高い。
      // ロジックで再ソートすると厳密解とは異なる可能性があるが、影響軽微と判断して許容する。
      tagSearchedArticles = tagSearchedArticles.sort(
        (a: any, b: any) => b.likeCount - a.likeCount,
      );
    }
    return tagSearchedArticles;
}
